import clsx from "clsx";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { ArrowDownIcon, ArrowUpIcon, CheckIcon, CrossIcon } from "../Icons";
import { Typo } from "../Typo";

export type MultiSelectOption = {
  label: string;
  value: string;
};

export type MultiSelectProps = {
  options: MultiSelectOption[];
  selected: MultiSelectOption[];
  onChange: (selected: MultiSelectOption[]) => void;
  label?: string;
  placeholder?: string;
  id?: string;
  variant?: "dropdown" | "expandable";
  hideDivider?: boolean;
  className?: string;
  dropUp?: boolean;
  defaultOpen?: boolean;
  /** When true, renders dropdown with high z-index to appear above modals */
  modal?: boolean;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "data-testid"?: string;
};

export const MultiSelect = ({
  options,
  selected,
  onChange,
  label,
  placeholder,
  id,
  variant = "dropdown",
  hideDivider = false,
  className,
  dropUp = false,
  defaultOpen = false,
  modal = false,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "data-testid": testId,
}: MultiSelectProps) => {
  const { t } = useTranslation();
  const translatedContent = {
    all: t("common_all", "All"),
    selected: t("common_selected", "selected"),
    selectAll: t("common_selectAll", "Select all"),
    pleaseSelect: t("common_pleaseSelect", "Please select"),
    clearSelection: t("common_clearSelection", "Clear selection"),
  };

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(selected.length);
  // Start with position off-screen to prevent flash at (0,0) before position is calculated
  const [dropdownPosition, setDropdownPosition] = useState({
    top: -9999,
    left: -9999,
    width: 0,
  });
  // Track when position has been calculated to avoid flash at wrong position on first render
  const [positionReady, setPositionReady] = useState(false);

  // Update dropdown position when modal mode is active
  useLayoutEffect(() => {
    if (!modal || !isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportHeight = window.innerHeight;
      const dropdownHeight = dropdownRef.current?.offsetHeight ?? 240;
      const spaceBelow = viewportHeight - rect.bottom - 8;
      const shouldDropUp = dropUp || spaceBelow < dropdownHeight;

      setDropdownPosition({
        top: shouldDropUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    // Find all scrollable parents to attach scroll listeners
    const getScrollableParents = (element: HTMLElement): HTMLElement[] => {
      const scrollableParents: HTMLElement[] = [];
      let parent = element.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        if (
          overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowX === "auto" ||
          overflowX === "scroll"
        ) {
          scrollableParents.push(parent);
        }
        parent = parent.parentElement;
      }
      return scrollableParents;
    };

    const scrollableParents = getScrollableParents(buttonRef.current);

    // Close dropdown when scrolling inside a scrollable parent (e.g. modal)
    // to prevent the dropdown from floating outside modal boundaries
    const closeOnScroll = () => setIsOpen(false);

    // Calculate initial position immediately (before paint)
    // This ensures the dropdown is positioned correctly from the start
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    // Use estimated height for initial calculation
    const estimatedDropdownHeight = 240;
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const shouldDropUp = dropUp || spaceBelow < estimatedDropdownHeight;

    // Set initial position synchronously
    setDropdownPosition({
      top: shouldDropUp
        ? rect.top - estimatedDropdownHeight - 4
        : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
    setPositionReady(false);

    // Use requestAnimationFrame to recalculate with actual height after render
    const frameId = requestAnimationFrame(() => {
      const actualRect = buttonRef.current?.getBoundingClientRect();
      if (!actualRect) return;

      const actualDropdownHeight =
        dropdownRef.current?.offsetHeight ?? estimatedDropdownHeight;
      const actualSpaceBelow = viewportHeight - actualRect.bottom - 8;
      const actualShouldDropUp =
        dropUp || actualSpaceBelow < actualDropdownHeight;

      setDropdownPosition({
        top: actualShouldDropUp
          ? actualRect.top - actualDropdownHeight - 4
          : actualRect.bottom + 4,
        left: actualRect.left,
        width: actualRect.width,
      });
      setPositionReady(true);
    });

    // Close dropdown when scrolling inside parent containers
    for (const parent of scrollableParents) {
      parent.addEventListener("scroll", closeOnScroll);
    }
    window.addEventListener("resize", updatePosition);

    return () => {
      cancelAnimationFrame(frameId);
      for (const parent of scrollableParents) {
        parent.removeEventListener("scroll", closeOnScroll);
      }
      window.removeEventListener("resize", updatePosition);
    };
  }, [modal, isOpen, dropUp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer =
        containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown =
        !dropdownRef.current || !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset position state when dropdown closes to prevent flash at old position on reopen
  useEffect(() => {
    if (!isOpen) {
      setPositionReady(false);
      setDropdownPosition({ top: -9999, left: -9999, width: 0 });
    }
  }, [isOpen]);

  const calculateVisibleCount = useCallback(() => {
    if (!tagsContainerRef.current || selected.length === 0) {
      setVisibleCount(selected.length);
      return;
    }

    const container = tagsContainerRef.current;
    const children = Array.from(container.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );

    children.forEach((child) => {
      child.style.setProperty("display", "inline-flex", "important");
    });

    const containerWidth = container.offsetWidth;
    const OVERFLOW_BADGE_WIDTH = 70;
    const TAG_GAP = 4;
    const widths = children.map((child) => child.offsetWidth + TAG_GAP);
    const totalWidth = widths.reduce((sum, width) => sum + width, 0);

    children.forEach((child) => {
      child.style.removeProperty("display");
    });

    // Only reserve room for the "+N more" badge once we know everything
    // won't fit without it -- otherwise the reservation itself can push an
    // otherwise-fitting last chip over the edge.
    if (totalWidth <= containerWidth) {
      setVisibleCount(selected.length);
      return;
    }

    let accumulatedWidth = 0;
    let count = 0;
    for (const childWidth of widths) {
      if (
        accumulatedWidth + childWidth >
        containerWidth - OVERFLOW_BADGE_WIDTH
      ) {
        break;
      }
      accumulatedWidth += childWidth;
      count++;
    }

    setVisibleCount(Math.max(1, count));
  }, [selected.length]);

  const calculateVisibleCountRef = useRef(calculateVisibleCount);
  useEffect(() => {
    calculateVisibleCountRef.current = calculateVisibleCount;
  }, [calculateVisibleCount]);

  useLayoutEffect(() => {
    calculateVisibleCount();
  }, [selected, calculateVisibleCount]);

  const hasSelection = selected.length > 0;

  // Scoped to real container resizes (window resize, modal reflow) -- content
  // changes are already handled deterministically by the layout effect above.
  // Re-subscribing on every selection change would race the initial
  // ResizeObserver callback (fired async, per spec) against that sync pass.
  useEffect(() => {
    if (!tagsContainerRef.current) return;

    const observer = new ResizeObserver(() => {
      calculateVisibleCountRef.current();
    });
    observer.observe(tagsContainerRef.current);

    return () => observer.disconnect();
  }, [hasSelection]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleCheckboxChange = (option: MultiSelectOption) => {
    const isSelected = selected.some((s) => s.value === option.value);
    if (isSelected) {
      onChange(selected.filter((s) => s.value !== option.value));
    } else {
      onChange([...selected, option]);
    }
  };

  const handleClearSelection = () => {
    onChange([]);
  };

  const handleRemoveTag = (e: React.MouseEvent, option: MultiSelectOption) => {
    e.stopPropagation();
    onChange(selected.filter((s) => s.value !== option.value));
  };

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const overflowCount = selected.length - visibleCount;

  const summaryLabel =
    selected.length === options.length
      ? translatedContent.all
      : `${selected.length} ${translatedContent.selected}`;

  if (variant === "expandable") {
    return (
      <div
        className={clsx(
          "flex w-full flex-col py-4",
          hideDivider ? "pb-0" : "border-b border-stroke-primary",
          className,
        )}
      >
        <div
          className="flex cursor-pointer items-center justify-between py-2"
          onClick={handleToggle}
        >
          <Typo element="p" type="labelSM">
            {label}
          </Typo>
          <button type="button" className="flex items-center gap-1">
            <Typo element="span" type="bodySM" textColor="text-text-selected">
              {summaryLabel}
            </Typo>
            {isOpen ? (
              <ArrowUpIcon className="h-2 w-2 text-icon-primary" />
            ) : (
              <ArrowDownIcon className="h-2 w-2 text-icon-primary" />
            )}
          </button>
        </div>

        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-out ${
            isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className={`overflow-hidden ${isOpen ? "mt-2" : "mt-0"}`}>
            <div className="space-y-0">
              <label className="block">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={selected.length === options.length}
                      onChange={handleSelectAll}
                      className="peer sr-only"
                    />
                    <div className="flex h-6 w-6 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                      {selected.length === options.length && (
                        <CheckIcon className="h-3 w-3 text-white" />
                      )}
                    </div>
                  </div>
                  <Typo element="span" type="bodySM">
                    {translatedContent.selectAll}
                  </Typo>
                </div>
              </label>

              {options.map((option) => {
                const isChecked = selected.some(
                  (s) => s.value === option.value,
                );
                return (
                  <label key={option.value} className="block pt-3">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(option)}
                          className="peer sr-only"
                        />
                        <div className="flex h-6 w-6 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                          {isChecked && (
                            <CheckIcon className="h-3 w-3 text-white" />
                          )}
                        </div>
                      </div>
                      <Typo element="span" type="bodySM">
                        {option.label}
                      </Typo>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TODO: extract into a module-level presentational component (e.g. MultiSelectDropdown)
  const renderDropdown = () => {
    const dropdownContent = (
      <div
        ref={dropdownRef}
        className={clsx(
          "rounded-lg border border-stroke-primary bg-white shadow-lg",
          modal ? "fixed z-floating" : "absolute z-20 w-full",
          !modal && (dropUp ? "bottom-full mb-1" : "mt-1"),
        )}
        style={
          modal
            ? {
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                // Use visibility:hidden to completely hide until position is ready
                // This prevents any flash while still allowing height measurement
                visibility: positionReady ? "visible" : "hidden",
              }
            : undefined
        }
      >
        <ul
          role="listbox"
          aria-multiselectable="true"
          className="max-h-[240px] overflow-y-auto p-2"
        >
          <li role="option" aria-selected={selected.length === options.length}>
            <label className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-overlay-opacity-state-hover">
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  checked={selected.length === options.length}
                  onChange={handleSelectAll}
                  className="peer sr-only"
                  data-testid="multi_select_select_all_checkbox"
                />
                <div className="flex h-5 w-5 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                  {selected.length === options.length && (
                    <CheckIcon className="h-3 w-3 text-white" />
                  )}
                </div>
              </div>
              <Typo element="span" type="bodySM">
                {translatedContent.selectAll}
              </Typo>
            </label>
          </li>
          {options.map((option) => {
            const isChecked = selected.some((s) => s.value === option.value);
            return (
              <li key={option.value} role="option" aria-selected={isChecked}>
                <label className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-overlay-opacity-state-hover">
                  <div className="relative flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleCheckboxChange(option)}
                      className="peer sr-only"
                      data-testid={`multi_select_option_${option.value}_checkbox`}
                    />
                    <div className="flex h-5 w-5 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                      {isChecked && (
                        <CheckIcon className="h-3 w-3 text-white" />
                      )}
                    </div>
                  </div>
                  <Typo element="span" type="bodySM">
                    {option.label}
                  </Typo>
                </label>
              </li>
            );
          })}
        </ul>
        {hasSelection && (
          <div className="mx-3 border-t border-stroke-primary py-2">
            <button
              type="button"
              onClick={handleClearSelection}
              data-testid="multi_select_clear_selection_button"
              className="text-text-secondary"
            >
              <Typo
                element="span"
                type="label2XS"
                textColor="text-text-secondary"
              >
                {translatedContent.clearSelection}
              </Typo>
            </button>
          </div>
        )}
      </div>
    );

    return modal
      ? createPortal(dropdownContent, document.body)
      : dropdownContent;
  };

  return (
    <div className="flex w-full flex-col gap-2" ref={containerRef}>
      {label && (
        <Typo element="p" type="labelSM">
          {label}
        </Typo>
      )}
      <div className="relative">
        <button
          ref={buttonRef}
          id={id}
          type="button"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          data-testid={testId}
          className={`flex min-h-[40px] w-full items-center gap-2 truncate rounded-lg bg-surface-tonal-default px-3 py-2 text-left transition-colors ${
            isOpen
              ? "border-2 border-stroke-focused"
              : "hover:bg-surface-tonal-bold"
          }`}
        >
          {hasSelection ? (
            <div
              className={`flex min-w-0 flex-1 items-center gap-1 ${
                isOpen ? "flex-wrap" : "overflow-hidden"
              }`}
            >
              <div
                ref={tagsContainerRef}
                className={`flex min-w-0 flex-1 items-center gap-1 ${
                  isOpen ? "flex-wrap" : "overflow-hidden"
                }`}
              >
                {selected.map((option, index) => (
                  <span
                    key={option.value}
                    className={`inline-flex flex-shrink-0 items-center gap-0.5 rounded-full border border-stroke-stroke bg-white px-2 py-0.5 ${
                      !isOpen && index >= visibleCount ? "hidden" : ""
                    }`}
                  >
                    <Typo
                      element="span"
                      type="label2XS"
                      className={`truncate ${!isOpen && overflowCount > 0 ? "max-w-[320px]" : "max-w-[360px]"}`}
                    >
                      {option.label}
                    </Typo>
                    <CrossIcon
                      className="h-3 w-3 cursor-pointer text-icon-primary"
                      onClick={(e: React.MouseEvent) =>
                        handleRemoveTag(e, option)
                      }
                    />
                  </span>
                ))}
              </div>
              {!isOpen && overflowCount > 0 && (
                <span className="flex-shrink-0 text-text-link-default underline">
                  <Typo
                    element="span"
                    type="bodyXS"
                    textColor="text-text-link-default"
                  >
                    {t("common_moreCount", {
                      count: overflowCount,
                      defaultValue: `+${overflowCount} more`,
                    })}
                  </Typo>
                </span>
              )}
            </div>
          ) : (
            <Typo element="span" type="bodySM" textColor="text-text-tertiary">
              {placeholder ?? translatedContent.pleaseSelect}
            </Typo>
          )}
          {isOpen ? (
            <ArrowUpIcon className="ml-auto h-3 w-3 flex-shrink-0 text-icon-primary" />
          ) : (
            <ArrowDownIcon className="ml-auto h-3 w-3 flex-shrink-0 text-icon-primary" />
          )}
        </button>

        {isOpen && renderDropdown()}
      </div>
    </div>
  );
};
